"use client";

/**
 * STORY-NEW-043: Refatorado para usar componentes de layout padronizados
 */

import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import {
  useGetUnifiedCampaignByCodeQuery,
  useUpdatePro013CampaignMutation,
} from "@/services/campaign";
import { useToast } from "components/ui/use-toast";
import { format } from "date-fns";
import { Plus, X, Package, List } from "lucide-react";
import { getTranslatedErrorMessage } from "../../utils/error-messages";
import { MESSAGES, NO_CHANGES_TOAST } from "../../utils/messages";
import {
  CampaignConflictModal,
  type CampaignConflictItem,
} from "../campaign-conflict-modal";
import {
  CampaignEditModalLayout,
  ModalLoadingState,
  ModalErrorState,
  ModalFieldGroup,
} from "./modal-layout";
import { CampaignStoresEditor } from "./campaign-stores-editor";

interface EditPro013ModalProps {
  campaignCode: number;
}

export function EditPro013Modal({ campaignCode }: EditPro013ModalProps) {
  const { toast } = useToast();
  const { handleModal } = useDialogModal();

  const { data: campaign, isLoading, error } = useGetUnifiedCampaignByCodeQuery(
    `PRO013-${campaignCode}`
  );

  const [updateCampaign, { isLoading: isUpdating }] = useUpdatePro013CampaignMutation();

  // Form state
  const [description, setDescription] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [departments, setDepartments] = useState<number[]>([]);
  const [groups, setGroups] = useState<number[]>([]);
  const [deptInput, setDeptInput] = useState("");
  const [groupInput, setGroupInput] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [conflicts, setConflicts] = useState<CampaignConflictItem[]>([]);
  const [showConflictModal, setShowConflictModal] = useState(false);

  // Load campaign data
  useEffect(() => {
    if (campaign) {
      setDescription(campaign.description || "");
      setStartDate(campaign.startDate ? format(new Date(campaign.startDate), "yyyy-MM-dd") : "");
      setEndDate(campaign.endDate ? format(new Date(campaign.endDate), "yyyy-MM-dd") : "");
      setDepartments(campaign.departments?.map((d: { code: number }) => d.code) || []);
      setGroups(campaign.groups?.map((g: { code: number }) => g.code) || []);
    }
  }, [campaign]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!description || description.trim().length < 3) {
      newErrors.description = "Descrição deve ter no mínimo 3 caracteres";
    }
    if (description.length > 100) {
      newErrors.description = "Descrição deve ter no máximo 100 caracteres";
    }
    if (!startDate) {
      newErrors.startDate = "Data de início é obrigatória";
    }
    if (!endDate) {
      newErrors.endDate = "Data de término é obrigatória";
    }
    if (startDate && endDate && new Date(endDate) < new Date(startDate)) {
      newErrors.endDate = "Data de término deve ser maior ou igual à data de início";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      // Diff payload — só envia campos modificados.
      const originalStartDate = campaign?.startDate
        ? format(new Date(campaign.startDate), "yyyy-MM-dd")
        : "";
      const originalEndDate = campaign?.endDate
        ? format(new Date(campaign.endDate), "yyyy-MM-dd")
        : "";
      const originalDepartments = (campaign?.departments?.map((d: { code: number }) => d.code) ?? []).slice().sort();
      const originalGroups = (campaign?.groups?.map((g: { code: number }) => g.code) ?? []).slice().sort();
      const sortedDepartments = [...departments].sort();
      const sortedGroups = [...groups].sort();
      const arraysEqual = (a: number[], b: number[]) =>
        a.length === b.length && a.every((v, i) => v === b[i]);

      const updateData: {
        code: number;
        description?: string;
        startDate?: string;
        endDate?: string;
        departments?: number[];
        groups?: number[];
      } = { code: campaignCode };

      if (description !== (campaign?.description ?? "")) updateData.description = description;
      if (startDate !== originalStartDate) updateData.startDate = startDate;
      if (endDate !== originalEndDate) updateData.endDate = endDate;
      if (!arraysEqual(sortedDepartments, originalDepartments)) updateData.departments = departments;
      if (!arraysEqual(sortedGroups, originalGroups)) updateData.groups = groups;

      const changedFields = Object.keys(updateData).filter((k) => k !== "code");
      if (changedFields.length === 0) {
        toast(NO_CHANGES_TOAST);
        return;
      }

      await updateCampaign(updateData).unwrap();

      toast(MESSAGES.UPDATE.SUCCESS);

      handleModal({ isOpen: false, element: null, title: "", description: "" });
    } catch (err: unknown) {
      const typedErr = err as { status?: number; data?: { message?: string; messages?: string[]; conflicts?: CampaignConflictItem[] } };

      if (typedErr?.status === 409 && typedErr?.data?.conflicts) {
        setConflicts(typedErr.data.conflicts);
        setShowConflictModal(true);
        toast(MESSAGES.CONFLICT.ERROR);
        return;
      }

      const errorMessage = getTranslatedErrorMessage(
        typedErr,
        MESSAGES.UPDATE.ERROR.description,
      );

      toast({ ...MESSAGES.UPDATE.ERROR, description: errorMessage });
    }
  };

  const handleCancel = () => {
    handleModal({ isOpen: false, element: null, title: "", description: "" });
  };

  const handleCloseConflictModal = () => {
    setShowConflictModal(false);
    setConflicts([]);
  };

  const addDepartment = () => {
    const code = parseInt(deptInput);
    if (!isNaN(code) && code >= 1 && code <= 99 && !departments.includes(code)) {
      setDepartments([...departments, code]);
      setDeptInput("");
    }
  };

  const removeDepartment = (code: number) => {
    setDepartments(departments.filter((d) => d !== code));
  };

  const addGroup = () => {
    const code = parseInt(groupInput);
    if (!isNaN(code) && code >= 1 && code <= 99 && !groups.includes(code)) {
      setGroups([...groups, code]);
      setGroupInput("");
    }
  };

  const removeGroup = (code: number) => {
    setGroups(groups.filter((g) => g !== code));
  };

  const visualStatus = (campaign?.visualStatus ?? campaign?.status ?? "FUTURE") as string;
  const isFuture = visualStatus === "FUTURE";
  const isActive = visualStatus === "ACTIVE";
  const isExpired = visualStatus === "EXPIRED";

  // Loading state
  if (isLoading) {
    return <ModalLoadingState />;
  }

  // Error state
  if (error || !campaign) {
    const loadErrorMessage = getTranslatedErrorMessage(
      error as { status?: number; data?: { message?: string; messages?: string[] } },
      "Erro ao carregar campanha. Tente novamente."
    );
    return <ModalErrorState details={loadErrorMessage} />;
  }

  return (
    <CampaignEditModalLayout
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      isLoading={isUpdating}
    >
      {/* Descrição */}
      <div className="space-y-2">
        <Label htmlFor="description">Descrição *</Label>
        <Input
          id="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Descrição da campanha"
          maxLength={100}
        />
        <p className="text-xs text-muted-foreground">{description.length}/100 caracteres</p>
        {errors.description && (
          <p className="text-sm text-destructive">{errors.description}</p>
        )}
      </div>

      {/* Datas — startDate desabilitado em ACTIVE/EXPIRED */}
      <ModalFieldGroup columns={2}>
        <div className="space-y-2">
          <Label htmlFor="startDate">Data de Início *</Label>
          <Input
            id="startDate"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            disabled={!isFuture}
            title={!isFuture ? "Data de início não pode ser alterada em campanhas ativas/expiradas" : undefined}
          />
          {!isFuture && (
            <p className="text-xs text-muted-foreground">
              Não é possível alterar a data de início em campanhas {isActive ? "ativas" : "expiradas"}.
            </p>
          )}
          {errors.startDate && (
            <p className="text-sm text-destructive">{errors.startDate}</p>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="endDate">Data de Término *</Label>
          <Input
            id="endDate"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            min={campaign?.startDate ? format(new Date(campaign.startDate), "yyyy-MM-dd") : undefined}
            disabled={isExpired}
          />
          {errors.endDate && (
            <p className="text-sm text-destructive">{errors.endDate}</p>
          )}
        </div>
      </ModalFieldGroup>

      {/* Departamentos */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Package className="h-4 w-4" />
          <Label>Departamentos</Label>
        </div>
        <div className="flex gap-2">
          <Input
            type="number"
            min="1"
            max="99"
            placeholder="Código (1-99)"
            value={deptInput}
            onChange={(e) => setDeptInput(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && addDepartment()}
          />
          <Button type="button" onClick={addDepartment} size="sm">
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        {departments.length > 0 && (
          <div className="flex flex-wrap gap-2 p-2 border rounded-md">
            {departments.map((code) => (
              <Badge key={code} variant="secondary">
                Dept {code}
                <button onClick={() => removeDepartment(code)} className="ml-1">
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}
      </div>

      {/* Grupos */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <List className="h-4 w-4" />
          <Label>Grupos de Produtos</Label>
        </div>
        <div className="flex gap-2">
          <Input
            type="number"
            min="1"
            max="99"
            placeholder="Código (1-99)"
            value={groupInput}
            onChange={(e) => setGroupInput(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && addGroup()}
          />
          <Button type="button" onClick={addGroup} size="sm">
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        {groups.length > 0 && (
          <div className="flex flex-wrap gap-2 p-2 border rounded-md">
            {groups.map((code) => (
              <Badge key={code} variant="secondary">
                Grupo {code}
                <button onClick={() => removeGroup(code)} className="ml-1">
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}
      </div>

      {/* Lojas — edição do conjunto com salvamento próprio (PATCH /stores) */}
      <CampaignStoresEditor
        type="PRO013"
        campaignCode={campaignCode}
        currentStores={campaign?.stores || []}
        allStores={!!campaign?.allStores}
        status={visualStatus}
      />

      <CampaignConflictModal
        isOpen={showConflictModal}
        conflicts={conflicts}
        onClose={handleCloseConflictModal}
        onGoBack={handleCloseConflictModal}
      />
    </CampaignEditModalLayout>
  );
}
